import React, { useState, useEffect } from 'react';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { countryNameFromCode } from '@scale/shared/types/countries';
import { Checkbox, Typography, Icon } from '@scale/scaleui';
import {
  COUNTRIES_BY_GROUP,
  FLAG_TEMPORARILY_ALLOWED_COUNTRIES,
  parseTemporarilyAllowedCountries,
  createGroupName,
} from '@scale/shared/fraud/constants';
import { twMerge } from 'tailwind-merge';
import { useUnleashBoolean, useUnleashVariant } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useUser } from 'react-scale/components/ui/UserContext';
import { cloneDeep } from 'lodash';
import { SelectMultiple } from '@scale/scaleui-radix';
import { useGetRandomConfig } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

interface SignupFlowCountrySelectProps {
  isCountryChecked: (countryCode: string) => boolean;
  handleCountryCheck: (countryCodes: string[]) => void;
  isDisabled?: (name: string) => boolean;
  className?: string;
  checkboxLabelClassName?: string;
}

interface SSOTCountryRow {
  CC: string;
  specialists_allowed: boolean;
  languages_allowed: boolean;
  coding_allowed: boolean;
}

const SignupFlowCountrySelect: React.FC<SignupFlowCountrySelectProps> = ({
  isCountryChecked,
  handleCountryCheck,
  isDisabled,
  className,
  checkboxLabelClassName,
}) => {
  const user = useUser();
  const isConfiguringG4CountriesAllowed = useUnleashBoolean(
    'allow-configuring-g4-signup-flows',
    { userId: user.id },
    { defaultValue: false },
  );

  const temporarilyAllowedCountriesVariant = useUnleashVariant(
    FLAG_TEMPORARILY_ALLOWED_COUNTRIES,
    {},
  );

  const countriesByGroup = React.useMemo(() => {
    if (!temporarilyAllowedCountriesVariant?.enabled) {
      return COUNTRIES_BY_GROUP;
    }

    const parsedCountries = parseTemporarilyAllowedCountries(
      temporarilyAllowedCountriesVariant?.payload?.value ?? '[]',
    );

    // Add countries from the feature flag by group
    const updatedCountriesByGroup = cloneDeep(COUNTRIES_BY_GROUP);

    if (Array.isArray(parsedCountries)) {
      // New array format
      parsedCountries.forEach(group => {
        updatedCountriesByGroup.push({
          label: group.label,
          countryCodes: [...group.countryCodes],
        });
      });
    } else {
      // Legacy object format
      Object.entries(parsedCountries).forEach(([groupName, countries]) => {
        updatedCountriesByGroup.push({
          label: groupName,
          countryCodes: [...countries],
        });
      });
    }

    return updatedCountriesByGroup;
  }, [temporarilyAllowedCountriesVariant]);

  // Memoize to avoid recomputing on every render
  const isGroupDisabled = React.useMemo(() => {
    if (!isConfiguringG4CountriesAllowed) {
      return (groupIndex: number) => {
        const group = countriesByGroup[groupIndex];
        return groupIndex >= 3 || isDisabled?.(group?.label ?? '');
      };
    }
    return (groupIndex: number) => {
      const group = countriesByGroup[groupIndex];
      return isDisabled?.(group?.label ?? '') ?? false;
    };
  }, [isDisabled, isConfiguringG4CountriesAllowed, countriesByGroup]);

  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { data: ssotConfigResponse } = useGetRandomConfig({
    randomConfigName: 'contributorssot',
  });

  const ssotConfig =
    ssotConfigResponse?.value && Array.isArray(ssotConfigResponse.value)
      ? ssotConfigResponse.value
      : null;

  const allChecked = (groupIndex: number) => {
    const group = countriesByGroup[groupIndex];
    return group ? group.countryCodes.every(isCountryChecked) : false;
  };

  const partiallyChecked = (groupIndex: number) => {
    const group = countriesByGroup[groupIndex];
    return group ? group.countryCodes.some(isCountryChecked) && !allChecked(groupIndex) : false;
  };

  const handleGroupCheck = (groupIndex: number) => {
    const group = countriesByGroup[groupIndex];
    if (!group) return;

    const groupChecked = allChecked(groupIndex);
    const countryCodesToCheck: string[] = [];
    group.countryCodes.forEach((countryCode: string) => {
      if (isCountryChecked(countryCode) === groupChecked) {
        countryCodesToCheck.push(countryCode);
      }
    });
    handleCountryCheck(countryCodesToCheck);
  };

  const handleGroupExpand = (groupIndex: number) => {
    setExpandedGroup(expandedGroup === groupIndex ? null : groupIndex);
  };

  const countSelectedCountries = (groupIndex: number) => {
    const group = countriesByGroup[groupIndex];
    return group ? group.countryCodes.filter(isCountryChecked).length : 0;
  };

  useEffect(() => {
    const countryCodesToUncheck: string[] = [];
    countriesByGroup.forEach((group, groupIndex) => {
      group.countryCodes.forEach((countryCode: string) => {
        if (
          isCountryChecked(countryCode) &&
          (isGroupDisabled(groupIndex) || isDisabled?.(countryCode))
        ) {
          countryCodesToUncheck.push(countryCode);
        }
      });
    });
    handleCountryCheck(countryCodesToUncheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupDisabled, countriesByGroup, isDisabled]);

  const handleCategoryChange = (newCategories: string[]) => {
    setSelectedCategories(newCategories);

    if (!ssotConfig) {
      return;
    }

    // Collect all currently checked countries to uncheck them first
    const allCurrentlyCheckedCountries: string[] = [];
    countriesByGroup.forEach(group => {
      group.countryCodes.forEach((countryCode: string) => {
        if (isCountryChecked(countryCode)) {
          allCurrentlyCheckedCountries.push(countryCode);
        }
      });
    });

    // If no categories selected, uncheck everything and return
    if (newCategories.length === 0) {
      if (allCurrentlyCheckedCountries.length > 0) {
        handleCountryCheck(allCurrentlyCheckedCountries);
      }
      return;
    }

    // Find countries allowed for ALL selected categories (logical AND)
    // Start with countries allowed for the first category, then intersect with each subsequent category
    const countryCodesToCheck: string[] = [];
    if (newCategories.length > 0) {
      const firstCategory = newCategories[0];
      const firstFieldName = `${firstCategory}_allowed` as keyof SSOTCountryRow;

      // Initialize with countries allowed for the first category
      ssotConfig.forEach((row: SSOTCountryRow) => {
        const categoryIsAllowed = row?.[firstFieldName];
        if (categoryIsAllowed) {
          countryCodesToCheck.push(row.CC);
        }
      });

      // Intersect with countries allowed for each remaining category
      for (let i = 1; i < newCategories.length; i++) {
        const category = newCategories[i];
        const fieldName = `${category}_allowed` as keyof SSOTCountryRow;

        // Keep only countries that are also allowed for this category
        const filteredCountries = countryCodesToCheck.filter(countryCode => {
          const row = ssotConfig.find((r: SSOTCountryRow) => r.CC === countryCode);
          const categoryIsAllowed = row?.[fieldName];
          return categoryIsAllowed;
        });

        // Replace the array with the filtered intersection
        countryCodesToCheck.length = 0;
        countryCodesToCheck.push(...filteredCountries);
      }
    }

    // Combine unchecking and checking into a single operation
    // We need to toggle OFF all currently checked countries, then toggle ON the countries we want
    // Since handleCountryCheck toggles, we need to pass:
    // 1. Countries that are checked but shouldn't be (uncheck them)
    // 2. Countries that aren't checked but should be (check them)

    const countriesToToggle: string[] = [];

    // Add countries that are currently checked but shouldn't be (to uncheck them)
    allCurrentlyCheckedCountries.forEach(countryCode => {
      if (!countryCodesToCheck.includes(countryCode)) {
        countriesToToggle.push(countryCode);
      }
    });

    // Add countries that should be checked but aren't currently (to check them)
    countryCodesToCheck.forEach(countryCode => {
      if (!allCurrentlyCheckedCountries.includes(countryCode)) {
        countriesToToggle.push(countryCode);
      }
    });

    if (countriesToToggle.length > 0) {
      handleCountryCheck(countriesToToggle);
    }
  };

  const ParagraphComponent =
    checkboxLabelClassName && !checkboxLabelClassName?.includes('xs')
      ? Typography.Paragraph2
      : Typography.Paragraph3;

  return (
    <div className={twMerge(className)}>
      <div className="mb-4">
        <SelectMultiple.Root
          size="1"
          value={selectedCategories}
          onValueChange={handleCategoryChange}
          disabled={!ssotConfig}
        >
          <SelectMultiple.Trigger placeholder="Bulk select countries for categories" />
          <SelectMultiple.Content>
            <SelectMultiple.Item value="specialists">Specialists</SelectMultiple.Item>
            <SelectMultiple.Item value="languages">Languages</SelectMultiple.Item>
            <SelectMultiple.Item value="coding">Coding</SelectMultiple.Item>
          </SelectMultiple.Content>
        </SelectMultiple.Root>
      </div>
      {countriesByGroup.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          <div className="flex items-center gap-1">
            <Checkbox
              checked={allChecked(groupIndex)}
              indeterminate={partiallyChecked(groupIndex)}
              onChange={() => handleGroupCheck(groupIndex)}
              disabled={isGroupDisabled(groupIndex)}
            />
            <ParagraphComponent className="whitespace-nowrap">
              {createGroupName(groupIndex, group.label)}
            </ParagraphComponent>
            <ParagraphComponent className="whitespace-nowrap text-neutral-950">
              {`${countSelectedCountries(groupIndex)}/${group.countryCodes.length} selected`}
            </ParagraphComponent>
            <Icon onClick={() => handleGroupExpand(groupIndex)} size="sm">
              {expandedGroup === groupIndex ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Icon>
          </div>
          <Collapse
            in={expandedGroup === groupIndex}
            timeout="auto"
            unmountOnExit
            sx={{ paddingLeft: 3 }}
          >
            <>
              {group.countryCodes.map((countryCode: string, countryIndex: number) => (
                <Checkbox
                  key={countryIndex + countryCode}
                  checked={isCountryChecked(countryCode)}
                  label={`${
                    countryNameFromCode(countryCode) ?? 'Unknown Country'
                  } - ${countryCode}`}
                  labelClassName={twMerge('p-0.5', checkboxLabelClassName ?? 'text-xs')}
                  name={countryCode}
                  onChange={() => handleCountryCheck([countryCode])}
                  disabled={isGroupDisabled(groupIndex) || isDisabled?.(countryCode)}
                />
              ))}
            </>
          </Collapse>
        </React.Fragment>
      ))}
    </div>
  );
};

export default SignupFlowCountrySelect;
